import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCompanion } from "@/companion/CompanionContext";
import { Link } from "react-router-dom";
import { api, boutiqueOf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Topbar from "@/components/Topbar";
import ZenotiImportDialog from "@/components/ZenotiImportDialog";
import { ChevronLeft, ChevronRight, Save, Check, Sparkles, Settings, Upload, X, Plus, DoorOpen, Hand, Calendar, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { celebrateSoft } from "@/lib/celebrate";
import { Skeleton } from "@/components/Skeleton";
import { useDesignMode, setOffset as designSetOffset, nudge as designNudge, rotateBy as designRotateBy, addCustom as designAddCustom, updateCustom as designUpdateCustom, nudgeCustom as designNudgeCustom, rotateCustomBy as designRotateCustomBy, deleteElement as designDeleteElement, isCustomId, getLayout as getLayoutSnapshot } from "@/lib/designMode";
import JourneyRoomsDesignOverlay from "@/components/JourneyRoomsDesignOverlay";
import OpenButton from "@/components/toto/OpenButton";

function iso(d) { return d.toISOString().slice(0, 10); }
function fmtDate(d) { return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }); }

const CHECK_KEYS = [
  { k: "call_am", label: "Call · AM" },
  { k: "call_pm", label: "Call · PM" },
  { k: "sweep_1", label: "Sweep 1" },
  { k: "sweep_2", label: "Sweep 2" },
  { k: "sweep_3", label: "Sweep 3" },
];

// 30-min slots 9:00 AM → 9:00 PM (kept for future time-lane view)
const ROOM_NUMBERS = Array.from({ length: 11 }, (_, i) => i + 1);

// Journey Rooms decorative votives that were removed during the Feb 18 tuning
// session (they collide with the bottom-corner hallway vessels). Baked-in
// alongside the CSS default offsets in index.css. To re-introduce one, remove
// its id from this set and update its default `--dx`/`--dy` in index.css.
const HIDDEN_VOTIVES = new Set(["votive-l-4", "votive-r-5"]);

const ROOM_STATE_STYLES = {
  assigned:    { dot: "bg-emerald-500", tint: "bg-white",              label: "Assigned"          },
  available:   { dot: "bg-stone-300",   tint: "bg-stone-50/60",        label: "Ready"             },
  turnover:    { dot: "bg-orange-400",  tint: "bg-orange-50",          label: "Being turned over" },
  laundry:     { dot: "bg-amber-500",   tint: "bg-amber-50",           label: "Laundry"           },
  maintenance: { dot: "bg-sky-500",     tint: "bg-sky-50",             label: "Maintenance"       },
  closed:      { dot: "bg-stone-700",   tint: "bg-stone-100 opacity-70", label: "Closed"          },
};

/**
 * Auto-assign therapists to rooms. Three explicit steps:
 *   1. Skip rooms that are unavailable (laundry / maintenance / closed).
 *   2. Honor therapist room preferences.
 *   3. Distribute remaining therapists into the lowest-numbered free rooms.
 *
 * Returns { rooms, summary } — summary is a short human-readable breakdown
 * shown as a status line beneath the Auto-assign action.
 */
function autoAssignRooms(mts, prefs, currentRooms) {
  const rooms = { ...(currentRooms || {}) };
  const unavailable = new Set(
    ROOM_NUMBERS.filter((rn) => {
      const s = rooms[String(rn)]?.state;
      return s === "laundry" || s === "maintenance" || s === "closed";
    })
  );
  // Clear existing therapist assignments; keep unavailable-room state intact.
  for (const rn of ROOM_NUMBERS) {
    const key = String(rn);
    if (unavailable.has(rn)) continue;
    const existing = rooms[key] || {};
    rooms[key] = { ...existing, state: "available", mt_id: null, mt_name: null, shift: null };
  }

  const usedRooms = new Set(unavailable);
  const usedMTs = new Set();
  let honored = 0;

  // Step 2 — honor preferences (first preference that's free wins).
  for (const mt of mts) {
    const p = prefs.find((x) => x.employee_id === mt.employee_id);
    if (!p) continue;
    for (const rn of p.preferred_rooms || []) {
      if (!usedRooms.has(rn) && ROOM_NUMBERS.includes(rn)) {
        const key = String(rn);
        rooms[key] = {
          ...(rooms[key] || {}),
          state: "assigned",
          mt_id: mt.employee_id,
          mt_name: mt.employee_name,
          shift: mt.shift,
        };
        usedRooms.add(rn);
        usedMTs.add(mt.employee_id);
        honored += 1;
        break;
      }
    }
  }
  // Step 3 — fill the rest.
  let assigned = honored;
  for (const mt of mts) {
    if (usedMTs.has(mt.employee_id)) continue;
    const free = ROOM_NUMBERS.find((rn) => !usedRooms.has(rn));
    if (!free) break;
    const key = String(free);
    rooms[key] = {
      ...(rooms[key] || {}),
      state: "assigned",
      mt_id: mt.employee_id,
      mt_name: mt.employee_name,
      shift: mt.shift,
    };
    usedRooms.add(free);
    usedMTs.add(mt.employee_id);
    assigned += 1;
  }

  const summary = {
    assigned,
    unavailable: unavailable.size,
    preferences_honored: honored,
    unplaced: Math.max(0, mts.length - assigned),
  };
  return { rooms, summary };
}

// ------------- MT Preferences Dialog -------------
function PrefsDialog({ open, onClose, boutique, mts }) {
  const companion = useCompanion();
  const [prefs, setPrefs] = useState([]);
  const [saving, setSaving] = useState({});
  useEffect(() => {
    if (!open) return;
    api.get("/journey-rooms/preferences", { params: { boutique } })
      .then((r) => setPrefs(r.data || []))
      .catch(() => setPrefs([]));
  }, [open, boutique]);

  const setFor = (emp_id, rooms, notes) => {
    setPrefs((prev) => {
      const idx = prev.findIndex((p) => p.employee_id === emp_id);
      const nxt = idx >= 0 ? [...prev] : [...prev, { employee_id: emp_id, preferred_rooms: [], notes: "" }];
      const i = idx >= 0 ? idx : nxt.length - 1;
      nxt[i] = { ...nxt[i], preferred_rooms: rooms, notes };
      return nxt;
    });
  };

  const save = async (emp_id) => {
    const p = prefs.find((x) => x.employee_id === emp_id);
    if (!p) return;
    setSaving((s) => ({ ...s, [emp_id]: true }));
    try {
      await api.put("/journey-rooms/preferences", { employee_id: emp_id, preferred_rooms: p.preferred_rooms || [], notes: p.notes || "" });
      toast.success("Preference saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving((s) => ({ ...s, [emp_id]: false })); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 bg-stone-900/30 backdrop-blur-sm flex items-center justify-center p-6 mobile-modal-wrap" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[80vh] overflow-y-auto card-shadow mobile-modal-card" onClick={(e) => e.stopPropagation()} data-testid="prefs-dialog">
        <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Room preferences</div>
            <div className="font-serif-display text-xl text-stone-900 mt-1">{companion.productWordmark} tries these first when assigning rooms</div>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700 text-sm">Close</button>
        </div>
        <div className="p-6 space-y-4">
          {mts.length === 0 && <div className="text-sm text-stone-400 italic">No massage therapists on staff yet.</div>}
          {mts.map((mt) => {
            const p = prefs.find((x) => x.employee_id === mt.employee_id) || { preferred_rooms: [], notes: "" };
            return (
              <div key={mt.employee_id} className="border border-stone-100 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-stone-800">{mt.employee_name}</div>
                  <button onClick={() => save(mt.employee_id)} disabled={saving[mt.employee_id]} className="text-[11px] px-3 py-1 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40" data-testid={`pref-save-${mt.employee_id}`}>
                    {saving[mt.employee_id] ? "Saving…" : "Save"}
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {ROOM_NUMBERS.map((n) => {
                    const active = (p.preferred_rooms || []).includes(n);
                    const order = (p.preferred_rooms || []).indexOf(n);
                    return (
                      <button
                        key={n}
                        data-testid={`pref-${mt.employee_id}-room-${n}`}
                        onClick={() => {
                          const cur = p.preferred_rooms || [];
                          const nxt = cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n];
                          setFor(mt.employee_id, nxt, p.notes);
                        }}
                        className={`relative w-8 h-8 rounded-full text-xs transition-all ${active ? "bg-stone-900 text-white" : "bg-stone-50 text-stone-500 hover:bg-stone-100"}`}
                      >
                        {n}
                        {active && <span className="absolute -top-1 -right-1 text-[9px] bg-white text-stone-900 rounded-full w-4 h-4 flex items-center justify-center border border-stone-200">{order + 1}</span>}
                      </button>
                    );
                  })}
                </div>
                <input
                  value={p.notes || ""}
                  onChange={(e) => setFor(mt.employee_id, p.preferred_rooms || [], e.target.value)}
                  placeholder="Notes (e.g. quiet room, near sink)"
                  className="w-full px-3 py-1.5 rounded-full subtle-border text-xs outline-none focus:border-stone-400"
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────────── Journey Rooms · spatial map ───────────────
// (formerly "Boutique Floor" — renamed to Journey Rooms Feb 17. The class
// names `.boutique-floor-page`, `.room-door`, `.boutique-hallway` are kept
// intentionally: they're implementation, not user-facing.)
function BoutiqueFloor({ log, prefs, floorPlan, onChange, canEditAll, boutique }) {
  const companion = useCompanion();
  const mtsWorking = useMemo(
    () => (log.roster || []).filter((r) => r.role === "massage_therapist" && r.shift && r.shift.toUpperCase() !== "OFF"),
    [log.roster]
  );
  const rooms = log.rooms || {};
  const [summary, setSummary] = useState(null);
  const [popover, setPopover] = useState(null); // room id currently open
  const viewportRef = useRef(null);

  // ── Horizontal presentation mode ─────────────────────────────────────────
  // Presentation-layer only — same room data, same ids, same popovers.
  // Toggled via `?horizontal=1` OR a header button. Vertical remains default
  // until this variant is reviewed and approved.
  const [horizontal, setHorizontal] = useState(() => {
    if (typeof window === "undefined") return false;
    const p = new URLSearchParams(window.location.search);
    return p.get("horizontal") === "1";
  });

  // ── Journey Rooms Design Mode ────────────────────────────────────────────
  // In-app WYSIWYG for tuning decorative element placement. Enabled via
  // ?design=1. See /lib/designMode.js + <JourneyRoomsDesignOverlay />.
  const design = useDesignMode();
  const [selectedDesignId, setSelectedDesignId] = useState(null);
  const [gridOn, setGridOn] = useState(false);

  // Toggle body classes so global CSS affordances (outlines, grid) apply.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("journey-design-active", design.active);
    document.body.classList.toggle("journey-design-grid", design.active && gridOn);
    return () => {
      document.body.classList.remove("journey-design-active");
      document.body.classList.remove("journey-design-grid");
    };
  }, [design.active, gridOn]);

  // Global drag handler — click a [data-design-id] element to select, drag
  // to shift its offsets. Built-ins update --dx/--dy; custom elements update
  // their absolute x/y within the hallway canvas.
  //
  // Listener attaches to document.body so it doesn't depend on viewportRef
  // being populated at effect-run time — the viewport only mounts after
  // rooms load, and the design.layout dep alone won't re-run this effect
  // when that happens.
  useEffect(() => {
    if (!design.active) return;
    const onDown = (e) => {
      const target = e.target.closest("[data-design-id]");
      if (!target) {
        if (!e.target.closest("[data-testid='journey-design-overlay']")) {
          setSelectedDesignId(null);
        }
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const id = target.dataset.designId;
      setSelectedDesignId(id);
      const start = { x: e.clientX, y: e.clientY };
      const isCustom = isCustomId(id);
      // Read the latest state at drag-start via the module getters, not the
      // stale hook snapshot — avoids losing an in-flight drag when React
      // re-renders during nudging.
      const baseCustom = isCustom
        ? { ...(getLayoutSnapshot().custom.find((c) => c.id === id) || {}) }
        : null;
      const baseOffset = !isCustom ? { ...(getLayoutSnapshot().offsets[id] || {}) } : null;
      const onMove = (ev) => {
        const ddx = ev.clientX - start.x;
        const ddy = ev.clientY - start.y;
        if (isCustom && baseCustom) {
          designUpdateCustom(id, { x: (baseCustom.x || 0) + ddx, y: (baseCustom.y || 0) + ddy });
        } else if (baseOffset) {
          designSetOffset(id, { dx: (baseOffset.dx || 0) + ddx, dy: (baseOffset.dy || 0) + ddy });
        }
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    };
    document.body.addEventListener("mousedown", onDown);
    return () => document.body.removeEventListener("mousedown", onDown);
  }, [design.active]);

  // Keyboard nudges + delete for the selected element.
  useEffect(() => {
    if (!design.active || !selectedDesignId) return;
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const step = e.shiftKey ? 10 : 1;
      const isCustom = isCustomId(selectedDesignId);
      let handled = true;
      if (e.altKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        const drot = (e.key === "ArrowLeft" ? -1 : 1) * (e.shiftKey ? 5 : 1);
        if (isCustom) designRotateCustomBy(selectedDesignId, drot);
        else designRotateBy(selectedDesignId, drot);
      } else if (e.key === "ArrowLeft") isCustom ? designNudgeCustom(selectedDesignId, -step, 0) : designNudge(selectedDesignId, -step, 0);
      else if (e.key === "ArrowRight") isCustom ? designNudgeCustom(selectedDesignId, step, 0) : designNudge(selectedDesignId, step, 0);
      else if (e.key === "ArrowUp") isCustom ? designNudgeCustom(selectedDesignId, 0, -step) : designNudge(selectedDesignId, 0, -step);
      else if (e.key === "ArrowDown") isCustom ? designNudgeCustom(selectedDesignId, 0, step) : designNudge(selectedDesignId, 0, step);
      else if (e.key === "Delete" || e.key === "Backspace") {
        designDeleteElement(selectedDesignId);
        setSelectedDesignId(null);
      }
      else if (e.key === "Escape") setSelectedDesignId(null);
      else handled = false;
      if (handled) e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [design.active, selectedDesignId]);

  // Add a new custom element at the top-left of the hallway canvas so the
  // user can spot it, then drag it into place.
  const handleAddCustom = (kind) => {
    // Find the hallway canvas so we can drop at a visible starting position.
    const canvasEl = viewportRef.current?.querySelector(".boutique-hallway");
    const rect = canvasEl?.getBoundingClientRect();
    const startX = rect ? 60 : 60;
    const startY = rect ? 60 : 60;
    const newId = designAddCustom({ kind, x: startX, y: startY });
    setSelectedDesignId(newId);
    toast.success(`Added ${kind} — drag into place`);
  };

  // On mount (and when the layout size changes) center the hallway inside the
  // scrollable viewport so mobile users see both sides at once.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const overflow = el.scrollWidth - el.clientWidth;
    if (overflow > 0) el.scrollLeft = overflow / 2;
  }, [floorPlan?.layout?.length]);

  // Group layout by side. Skip inactive rooms — they don't appear on the floor.
  const layout = (floorPlan?.layout || []).filter((r) => r.active);
  const leftRooms  = layout.filter((r) => r.side === "left").sort((a, b) => a.position - b.position);
  const rightRooms = layout.filter((r) => r.side === "right").sort((a, b) => a.position - b.position);

  const autofill = () => {
    const { rooms: nextAll, summary: s } = autoAssignRoomsSpatial(mtsWorking, prefs, rooms, layout);

    const reduce = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    // Which rooms actually changed? Only those animate.
    const changedIds = layout
      .map((r) => r.id)
      .filter((id) => JSON.stringify(rooms[id] || {}) !== JSON.stringify(nextAll[id] || {}));

    if (reduce || changedIds.length === 0) {
      onChange({ ...log, rooms: nextAll });
      setSummary(s);
      toast.success(`Auto-assigned · ${s.assigned} therapists placed`);
      return;
    }

    // Choreograph: left column top→bottom, then right column top→bottom.
    // Each stage is ~180ms so the full ballet lands around 2s for a busy day.
    const orderedIds = [
      ...layout.filter((r) => r.side === "left").sort((a, b) => a.position - b.position).map((r) => r.id),
      ...layout.filter((r) => r.side === "right").sort((a, b) => a.position - b.position).map((r) => r.id),
    ].filter((id) => changedIds.includes(id));

    // Apply progressively — one room per tick — so the CSS curtain transition
    // (320ms) plays for each door in sequence rather than all at once.
    let working = { ...(log.rooms || {}) };
    orderedIds.forEach((id, idx) => {
      setTimeout(() => {
        working = { ...working, [id]: nextAll[id] };
        onChange({ ...log, rooms: { ...working } });
        if (idx === orderedIds.length - 1) {
          // Give the last curtain a beat to finish before showing the summary.
          setTimeout(() => {
            setSummary(s);
            toast.success(`Auto-assigned · ${s.assigned} therapists placed`);
          }, 320);
        }
      }, idx * 180);
    });
  };

  const patchRoom = (roomId, patch) => {
    const nxt = { ...(log.rooms || {}) };
    nxt[roomId] = { ...(nxt[roomId] || { state: "available" }), ...patch };
    if (patch.state && patch.state !== "assigned") {
      nxt[roomId].mt_id = null; nxt[roomId].mt_name = null; nxt[roomId].shift = null;
    }
    // "Ready around" timing only makes sense for laundry / turnover.
    if (patch.state && patch.state !== "laundry" && patch.state !== "turnover") {
      nxt[roomId].expected_at = null;
    }
    onChange({ ...log, rooms: nxt });
    // Never auto-close on state or timer changes. The popover is a workspace:
    // the user often clicks "Assigned" to reveal the therapist dropdown, or
    // switches to "Laundry" to then set a Ready-around time. Auto-closing
    // pulled the rug out. The popover closes only via the explicit paths:
    //   • picking a therapist (setRoomTherapist below)
    //   • the X button
    //   • the backdrop overlay
    //   • Escape (RoomPopover's keydown listener)
  };

  const setRoomTherapist = (roomId, mt_id) => {
    if (!mt_id) return patchRoom(roomId, { state: "available", mt_id: null, mt_name: null, shift: null });
    const mt = mtsWorking.find((m) => m.employee_id === mt_id);
    if (!mt) return;
    const nxt = { ...(log.rooms || {}) };
    for (const [k, v] of Object.entries(nxt)) {
      if (v?.mt_id === mt.employee_id && k !== roomId) {
        nxt[k] = { ...v, state: "available", mt_id: null, mt_name: null, shift: null };
      }
    }
    nxt[roomId] = {
      ...(nxt[roomId] || {}), state: "assigned",
      mt_id: mt.employee_id, mt_name: mt.employee_name, shift: mt.shift,
    };
    onChange({ ...log, rooms: nxt });
    setPopover(null);
  };

  const assignedIds = new Set(Object.values(rooms).map((r) => r?.mt_id).filter(Boolean));

  return (
    <section className="bg-white subtle-border rounded-2xl overflow-hidden card-shadow boutique-floor-page" data-testid="boutique-floor">
      <div className="px-6 py-3 border-b border-stone-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 floor-header">
        <div>
          <div className="font-serif-display text-lg text-stone-900">Journey Rooms</div>
          <div className="text-[11px] text-stone-400">The rooms, as they sit in the hallway.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap floor-actions">
          <Link
            to="/settings/floor"
            data-testid="floor-plan-settings-link"
            className="text-[11px] px-3 py-1.5 rounded-full subtle-border text-stone-600 hover:bg-stone-50 inline-flex items-center gap-1.5"
            title="Edit the floor plan"
            aria-label="Edit the Journey Rooms floor plan"
          >
            <Settings size={11} strokeWidth={1.5} /> Floor plan
          </Link>
          {canEditAll && (
            <button
              onClick={autofill}
              data-testid="rooms-autofill"
              className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-800"
              aria-label="Auto-assign therapists to Journey Rooms"
            >
              <Sparkles size={12} strokeWidth={1.5} /> {companion.chatAssistantName}, room us up
            </button>
          )}
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              if (horizontal) url.searchParams.delete("horizontal");
              else url.searchParams.set("horizontal", "1");
              window.history.replaceState({}, "", url.toString());
              setHorizontal((v) => !v);
            }}
            data-testid="journey-horizontal-toggle"
            className={`text-[11px] px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${
              horizontal
                ? "bg-stone-900 text-white hover:bg-stone-800"
                : "subtle-border text-stone-600 hover:bg-stone-50"
            }`}
            title="Toggle horizontal (landscape) presentation of the hallway"
          >
            {horizontal ? "Vertical view" : "Horizontal view"}
          </button>
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              if (design.active) url.searchParams.delete("design");
              else url.searchParams.set("design", "1");
              window.location.href = url.toString();
            }}
            data-testid="journey-design-toggle"
            className={`text-[11px] px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${
              design.active
                ? "bg-amber-500 text-stone-950 hover:bg-amber-400"
                : "subtle-border text-stone-600 hover:bg-stone-50"
            }`}
            title="Tune the exact placement of candles, vessels and rugs"
          >
            <Settings size={11} strokeWidth={1.5} /> {design.active ? "Exit design" : "Design mode"}
          </button>
        </div>
      </div>

      {summary && (
        <div className="px-6 py-2.5 text-[11px] text-stone-500 border-b border-stone-100 flex flex-wrap gap-x-4 gap-y-1" data-testid="rooms-autofill-summary">
          <span>Assigned <strong className="text-stone-800 tabular-nums">{summary.assigned}</strong></span>
          {summary.unavailable > 0 && <span>Unavailable <strong className="text-stone-800 tabular-nums">{summary.unavailable}</strong></span>}
          <span>Preferences honored <strong className="text-stone-800 tabular-nums">{summary.preferences_honored}</strong></span>
          {summary.specialty_reserved > 0 && <span>Specialty reserved <strong className="text-stone-800 tabular-nums">{summary.specialty_reserved}</strong></span>}
          {summary.unplaced > 0 && <span className="text-amber-700">Unplaced <strong className="tabular-nums">{summary.unplaced}</strong></span>}
        </div>
      )}

      {/* The hallway itself: you're standing at the Transition Door, looking down. */}
      <div className="p-4 sm:p-6">
        {layout.length === 0 ? (
          <div className="text-sm text-stone-400 italic py-8 text-center">
            No rooms defined for this boutique yet. <Link to="/settings/floor" className="text-stone-700 underline">Set up the floor plan.</Link>
          </div>
        ) : horizontal ? (
          <div className="floor-plan-viewport" ref={viewportRef} data-orientation="horizontal">
            <div className="floor-plan-canvas boutique-hallway horizontal-hallway p-3 sm:p-4">
              {/* LEFT door column — Transition Room Door + flanking vessels */}
              <div className="h-door-col h-door-left">
                <div
                  className="hallway-vessel"
                  aria-hidden="true"
                  data-design-id="vessel-tl"
                  data-design-selected={selectedDesignId === "vessel-tl" ? "true" : undefined}
                  style={{ ...(design.styleFor("vessel-tl") || {}), ...(design.isHidden("vessel-tl") ? { display: "none" } : {}) }}
                >
                  <span className="candle" /><span className="candle" /><span className="candle" />
                </div>
                <div className="arch-anchor h-arch-anchor">Transition Room Door</div>
                <div
                  className="hallway-vessel"
                  aria-hidden="true"
                  data-design-id="vessel-bl"
                  data-design-selected={selectedDesignId === "vessel-bl" ? "true" : undefined}
                  style={{ ...(design.styleFor("vessel-bl") || {}), ...(design.isHidden("vessel-bl") ? { display: "none" } : {}) }}
                >
                  <span className="candle" /><span className="candle" /><span className="candle" />
                </div>
              </div>

              {/* MAIN — top row of rooms, horizontal hallway, bottom row */}
              <div className="h-main">
                <div className="h-top-row room-col room-col-top relative">
                  {leftRooms.map((r) => (
                    <RoomDoor
                      key={r.id}
                      plan={r}
                      state={rooms[r.id] || { state: "available" }}
                      onOpen={() => canEditAll && setPopover(popover === r.id ? null : r.id)}
                      isOpen={popover === r.id}
                      mts={mtsWorking}
                      assignedIds={assignedIds}
                      onClose={() => setPopover(null)}
                      onSetTherapist={(mtId) => setRoomTherapist(r.id, mtId)}
                      onSetState={(s) => patchRoom(r.id, { state: s })}
                      onSetExpectedAt={(iso) => patchRoom(r.id, { expected_at: iso })}
                      canEdit={canEditAll}
                      side="top"
                      prefs={prefs}
                    />
                  ))}
                  {/* Votives at each top room's inside-bottom corner
                      (hallway-facing edge, translated from the vertical
                      "lower-inside corner" invariant). */}
                  {leftRooms.map((_, i) => {
                    const dId = `votive-l-${i}`;
                    if (HIDDEN_VOTIVES.has(dId)) return null;
                    if (design.isHidden(dId)) return null;
                    return (
                      <span
                        key={`hvt-${i}`}
                        aria-hidden="true"
                        className="candle-votive candle-divider candle-divider-h-top"
                        data-design-id={dId}
                        data-design-selected={selectedDesignId === dId ? "true" : undefined}
                        style={{ left: `${((i + 1) / leftRooms.length) * 100}%` }}
                      />
                    );
                  })}
                </div>

                <div className="h-center" aria-hidden="true">
                  <div className="hallway-rug hallway-rug-h" data-design-id="rug-1" data-design-selected={selectedDesignId === "rug-1" ? "true" : undefined} style={{ ...(design.styleFor("rug-1") || {}), ...(design.isHidden("rug-1") ? { display: "none" } : {}) }} />
                  <div className="hallway-rug hallway-rug-h" data-design-id="rug-2" data-design-selected={selectedDesignId === "rug-2" ? "true" : undefined} style={{ ...(design.styleFor("rug-2") || {}), ...(design.isHidden("rug-2") ? { display: "none" } : {}) }} />
                  <div className="hallway-rug hallway-rug-h" data-design-id="rug-3" data-design-selected={selectedDesignId === "rug-3" ? "true" : undefined} style={{ ...(design.styleFor("rug-3") || {}), ...(design.isHidden("rug-3") ? { display: "none" } : {}) }} />
                </div>

                <div className="h-bottom-row room-col room-col-bottom relative">
                  {rightRooms.map((r) => (
                    <RoomDoor
                      key={r.id}
                      plan={r}
                      state={rooms[r.id] || { state: "available" }}
                      onOpen={() => canEditAll && setPopover(popover === r.id ? null : r.id)}
                      isOpen={popover === r.id}
                      mts={mtsWorking}
                      assignedIds={assignedIds}
                      onClose={() => setPopover(null)}
                      onSetTherapist={(mtId) => setRoomTherapist(r.id, mtId)}
                      onSetState={(s) => patchRoom(r.id, { state: s })}
                      onSetExpectedAt={(iso) => patchRoom(r.id, { expected_at: iso })}
                      canEdit={canEditAll}
                      side="bottom"
                      prefs={prefs}
                    />
                  ))}
                  {rightRooms.map((_, i) => {
                    const dId = `votive-r-${i}`;
                    if (HIDDEN_VOTIVES.has(dId)) return null;
                    if (design.isHidden(dId)) return null;
                    return (
                      <span
                        key={`hvb-${i}`}
                        aria-hidden="true"
                        className="candle-votive candle-divider candle-divider-h-bottom"
                        data-design-id={dId}
                        data-design-selected={selectedDesignId === dId ? "true" : undefined}
                        style={{ left: `${((i + 1) / rightRooms.length) * 100}%` }}
                      />
                    );
                  })}
                </div>
              </div>

              {/* RIGHT door column — Restroom Vestibule Door + flanking vessels */}
              <div className="h-door-col h-door-right">
                <div
                  className="hallway-vessel"
                  aria-hidden="true"
                  data-design-id="vessel-tr"
                  data-design-selected={selectedDesignId === "vessel-tr" ? "true" : undefined}
                  style={{ ...(design.styleFor("vessel-tr") || {}), ...(design.isHidden("vessel-tr") ? { display: "none" } : {}) }}
                >
                  <span className="candle" /><span className="candle" /><span className="candle" />
                </div>
                <div className="arch-anchor h-arch-anchor">Restroom Vestibule Door</div>
                <div
                  className="hallway-vessel"
                  aria-hidden="true"
                  data-design-id="vessel-br"
                  data-design-selected={selectedDesignId === "vessel-br" ? "true" : undefined}
                  style={{ ...(design.styleFor("vessel-br") || {}), ...(design.isHidden("vessel-br") ? { display: "none" } : {}) }}
                >
                  <span className="candle" /><span className="candle" /><span className="candle" />
                </div>
              </div>

              {/* Custom design-mode elements — same as vertical view */}
              {(design.layout.custom || []).map((c) => {
                const cls = c.kind === "votive" ? "candle-votive" : c.kind === "vessel" ? "hallway-vessel" : "hallway-rug";
                return (
                  <div key={c.id} aria-hidden="true" className={cls}
                    data-design-id={c.id}
                    data-design-selected={selectedDesignId === c.id ? "true" : undefined}
                    style={design.styleForCustom(c)}>
                    {c.kind === "vessel" && (<><span className="candle" /><span className="candle" /><span className="candle" /></>)}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="floor-plan-viewport" ref={viewportRef}>
            <div className="floor-plan-canvas boutique-hallway p-4 sm:p-6">
            {/* Top doorway: symmetrical vessels flank the Transition Room Door.
                Never place a vessel in front of the opening — the walking path
                through the doorway must stay unobstructed. */}
            <div className="hallway-door-row">
              <div
                className="hallway-vessel"
                aria-hidden="true"
                data-design-id="vessel-tl"
                data-design-selected={selectedDesignId === "vessel-tl" ? "true" : undefined}
                style={{ ...(design.styleFor("vessel-tl") || {}), ...(design.isHidden("vessel-tl") ? { display: "none" } : {}) }}
              >
                <span className="candle" /><span className="candle" /><span className="candle" />
              </div>
              <div className="arch-anchor">Transition Room Door</div>
              <div
                className="hallway-vessel"
                aria-hidden="true"
                data-design-id="vessel-tr"
                data-design-selected={selectedDesignId === "vessel-tr" ? "true" : undefined}
                style={{ ...(design.styleFor("vessel-tr") || {}), ...(design.isHidden("vessel-tr") ? { display: "none" } : {}) }}
              >
                <span className="candle" /><span className="candle" /><span className="candle" />
              </div>
            </div>

            <div className="grid grid-cols-[1fr_100px_1fr] sm:grid-cols-[1fr_160px_1fr] gap-3 sm:gap-5 items-stretch mt-3">
              {/* LEFT SIDE — rooms facing right. flex-column so a sparser side
                  stretches taller to balance the opposite column's height. */}
              <div className="flex flex-col gap-3 min-h-0 relative room-col room-col-left">
                {leftRooms.map((r) => (
                  <RoomDoor
                    key={r.id}
                    plan={r}
                    state={rooms[r.id] || { state: "available" }}
                    onOpen={() => canEditAll && setPopover(popover === r.id ? null : r.id)}
                    isOpen={popover === r.id}
                    mts={mtsWorking}
                    assignedIds={assignedIds}
                    onClose={() => setPopover(null)}
                    onSetTherapist={(mtId) => setRoomTherapist(r.id, mtId)}
                    onSetState={(s) => patchRoom(r.id, { state: s })}
                    onSetExpectedAt={(iso) => patchRoom(r.id, { expected_at: iso })}
                    canEdit={canEditAll}
                    side="left"
                    prefs={prefs}
                  />
                ))}
                {/* One votive per room, anchored at the room's lower-inside
                    corner (bottom edge × hallway-facing edge). Just outside
                    the room card so it reads as an accent light sitting on
                    the hallway floor at that corner.
                    HIDDEN_VOTIVES skips those baked-out during the Feb 18
                    tuning session (they collide with the bottom-corner
                    vessels — see index.css). */}
                {leftRooms.map((_, i) => {
                  const dId = `votive-l-${i}`;
                  if (HIDDEN_VOTIVES.has(dId)) return null;
                  if (design.isHidden(dId)) return null;
                  return (
                    <span
                      key={`vl-${i}`}
                      aria-hidden="true"
                      className="candle-votive candle-divider candle-divider-left"
                      data-design-id={dId}
                      data-design-selected={selectedDesignId === dId ? "true" : undefined}
                      style={{ top: `${((i + 1) / leftRooms.length) * 100}%`, ...design.styleFor(dId) }}
                    />
                  );
                })}
              </div>

              {/* THE HALLWAY — three area rugs run down the center with bare
                  cement showing between them. Sconces have been moved to the
                  room-divider anchors on the adjacent columns. */}
              <div className="hallway-center" aria-hidden="true">
                <div className="hallway-rug" data-design-id="rug-1" data-design-selected={selectedDesignId === "rug-1" ? "true" : undefined} style={{ ...(design.styleFor("rug-1") || {}), ...(design.isHidden("rug-1") ? { display: "none" } : {}) }} />
                <div className="hallway-rug" data-design-id="rug-2" data-design-selected={selectedDesignId === "rug-2" ? "true" : undefined} style={{ ...(design.styleFor("rug-2") || {}), ...(design.isHidden("rug-2") ? { display: "none" } : {}) }} />
                <div className="hallway-rug" data-design-id="rug-3" data-design-selected={selectedDesignId === "rug-3" ? "true" : undefined} style={{ ...(design.styleFor("rug-3") || {}), ...(design.isHidden("rug-3") ? { display: "none" } : {}) }} />
              </div>

              {/* RIGHT SIDE — rooms facing left */}
              <div className="flex flex-col gap-3 min-h-0 relative room-col room-col-right">
                {rightRooms.map((r) => (
                  <RoomDoor
                    key={r.id}
                    plan={r}
                    state={rooms[r.id] || { state: "available" }}
                    onOpen={() => canEditAll && setPopover(popover === r.id ? null : r.id)}
                    isOpen={popover === r.id}
                    mts={mtsWorking}
                    assignedIds={assignedIds}
                    onClose={() => setPopover(null)}
                    onSetTherapist={(mtId) => setRoomTherapist(r.id, mtId)}
                    onSetState={(s) => patchRoom(r.id, { state: s })}
                    onSetExpectedAt={(iso) => patchRoom(r.id, { expected_at: iso })}
                    canEdit={canEditAll}
                    side="right"
                    prefs={prefs}
                  />
                ))}
                {/* One votive per room, anchored at the room's lower-inside
                    corner (bottom edge × hallway-facing edge). */}
                {rightRooms.map((_, i) => {
                  const dId = `votive-r-${i}`;
                  if (HIDDEN_VOTIVES.has(dId)) return null;
                  if (design.isHidden(dId)) return null;
                  return (
                    <span
                      key={`vr-${i}`}
                      aria-hidden="true"
                      className="candle-votive candle-divider candle-divider-right"
                      data-design-id={dId}
                      data-design-selected={selectedDesignId === dId ? "true" : undefined}
                      style={{ top: `${((i + 1) / rightRooms.length) * 100}%`, ...design.styleFor(dId) }}
                    />
                  );
                })}
              </div>
            </div>

            {/* Bottom doorway: symmetrical vessels flank the Restroom Vestibule
                Door. Same rule — never in front of the opening. */}
            <div className="hallway-door-row mt-3">
              <div
                className="hallway-vessel"
                aria-hidden="true"
                data-design-id="vessel-bl"
                data-design-selected={selectedDesignId === "vessel-bl" ? "true" : undefined}
                style={{ ...(design.styleFor("vessel-bl") || {}), ...(design.isHidden("vessel-bl") ? { display: "none" } : {}) }}
              >
                <span className="candle" /><span className="candle" /><span className="candle" />
              </div>
              <div className="arch-anchor">Restroom Vestibule Door</div>
              <div
                className="hallway-vessel"
                aria-hidden="true"
                data-design-id="vessel-br"
                data-design-selected={selectedDesignId === "vessel-br" ? "true" : undefined}
                style={{ ...(design.styleFor("vessel-br") || {}), ...(design.isHidden("vessel-br") ? { display: "none" } : {}) }}
              >
                <span className="candle" /><span className="candle" /><span className="candle" />
              </div>
            </div>
            {/* Custom design-mode elements — absolutely-positioned inside the
                hallway canvas. Rendered here so they sit above rooms and rugs
                but still inside the same relative container. */}
            {(design.layout.custom || []).map((c) => {
              const cls =
                c.kind === "votive"
                  ? "candle-votive"
                  : c.kind === "vessel"
                    ? "hallway-vessel"
                    : "hallway-rug";
              return (
                <div
                  key={c.id}
                  aria-hidden="true"
                  className={cls}
                  data-design-id={c.id}
                  data-design-selected={selectedDesignId === c.id ? "true" : undefined}
                  style={design.styleForCustom(c)}
                >
                  {c.kind === "vessel" && (
                    <>
                      <span className="candle" />
                      <span className="candle" />
                      <span className="candle" />
                    </>
                  )}
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>
      {design.active && (
        <JourneyRoomsDesignOverlay
          selectedId={selectedDesignId}
          onSelect={setSelectedDesignId}
          gridOn={gridOn}
          onToggleGrid={() => setGridOn((v) => !v)}
          onAdd={handleAddCustom}
          builtInInventory={[
            "vessel-tl", "vessel-tr", "vessel-bl", "vessel-br",
            ...leftRooms.map((_, i) => `votive-l-${i}`).filter((id) => !HIDDEN_VOTIVES.has(id)),
            ...rightRooms.map((_, i) => `votive-r-${i}`).filter((id) => !HIDDEN_VOTIVES.has(id)),
            "rug-1", "rug-2", "rug-3",
          ]}
        />
      )}
    </section>
  );
}

const ROOM_TYPE_LABEL = { standard: "", couples: "Couples", wet: "Wet Room", stretch: "Stretch", utility: "Utility" };

// Time helpers for the Ready-around field on laundry/turnover rooms.
// The input is a browser <input type="time"> using local HH:MM; we store an ISO
// timestamp for today so the "~N min" countdown reads correctly.
function timeLocalToIso(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}
function isoToTimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}
function readyAround(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.round((d - now) / 60000);
  if (Math.abs(diffMin) > 12 * 60) return null; // stale — ignore
  const hh = ((d.getHours() + 11) % 12) + 1;
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ampm = d.getHours() >= 12 ? "PM" : "AM";
  const clock = `${hh}:${mm} ${ampm}`;
  if (diffMin <= 0)      return { line: `Ready any moment`,          urgent: true  };
  if (diffMin <= 5)      return { line: `~${diffMin} min · ${clock}`, urgent: true  };
  return { line: `Ready around ${clock}`, urgent: false };
}

function RoomDoor({ plan, state, onOpen, isOpen, mts, assignedIds, onClose, onSetTherapist, onSetState, onSetExpectedAt, canEdit, side, prefs = [] }) {
  const style = ROOM_STATE_STYLES[state.state] || ROOM_STATE_STYLES.available;
  const isSpecialty = plan.type !== "standard";
  const isAssigned  = state.state === "assigned" && state.mt_name;
  const timing = (state.state === "laundry" || state.state === "turnover") ? readyAround(state.expected_at) : null;

  // Peek — hover on desktop, long-press on mobile. Quiet, read-only.
  const [peekOpen, setPeekOpen] = useState(false);
  const pressTimerRef = useRef(null);
  const longPressedRef = useRef(false);

  const startPress = () => {
    longPressedRef.current = false;
    clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      longPressedRef.current = true;
      setPeekOpen(true);
    }, 500);
  };
  const cancelPress = () => { clearTimeout(pressTimerRef.current); };
  const closePeek = () => setPeekOpen(false);
  const handleClick = (e) => {
    if (longPressedRef.current) {
      // Long-press triggered peek; suppress the click that follows on touchend.
      e.preventDefault();
      longPressedRef.current = false;
      return;
    }
    onOpen && onOpen();
  };

  // Was this room preferred by the therapist currently in it?
  const roomNumber = (() => {
    const m = plan.name.match(/^Room\s+(\d+)/i);
    return m ? parseInt(m[1], 10) : null;
  })();
  const isPreferredForCurrent =
    isAssigned && roomNumber != null &&
    (prefs.find((p) => p.employee_id === state.mt_id)?.preferred_rooms || []).includes(roomNumber);

  return (
    <div
      className="relative flex-1 flex flex-col min-h-0"
      data-testid={`floor-room-${plan.id}`}
      onMouseEnter={() => setPeekOpen(true)}
      onMouseLeave={closePeek}
      onTouchStart={startPress}
      onTouchEnd={cancelPress}
      onTouchCancel={cancelPress}
      onTouchMove={cancelPress}
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={!canEdit}
        data-testid={`floor-tile-${plan.id}`}
        aria-label={`${plan.name} — ${isAssigned ? state.mt_name : style.label}${timing ? `. ${timing.line}` : ""}`}
        className={`room-door w-full text-left ${isSpecialty ? "specialty" : ""}`}
        data-state={state.state || "available"}
        data-side={side}
      >
        {/* One linen curtain per room, gathered against the outer wall by a
            simple rope tie-back. Left rooms gather LEFT; right rooms gather
            RIGHT — see .room-door[data-side] rules in index.css. */}
        <div className="curtain" aria-hidden="true">
          <span className="rope" aria-hidden="true" />
        </div>

        {/* Caption sits above the curtains */}
        <div className="caption">
          <div className="flex items-baseline justify-between gap-2">
            <div className="min-w-0">
              <div className="name truncate">{plan.name}</div>
              {ROOM_TYPE_LABEL[plan.type] && <div className="subtype truncate">{ROOM_TYPE_LABEL[plan.type]}</div>}
            </div>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot}`} aria-hidden="true" />
          </div>

          {isAssigned ? (
            <>
              <div className="occupant" key={`o-${state.mt_id}`} title={state.mt_name}>{state.mt_name}</div>
              {state.shift && <div className="shift" key={`s-${state.mt_id}`}>{state.shift}</div>}
            </>
          ) : (
            <>
              <div className="state" key={`x-${state.state}`}>{style.label}</div>
              {timing && (
                <div
                  data-testid={`floor-timing-${plan.id}`}
                  className={`text-[10px] mt-1 ${timing.urgent ? "text-amber-700" : "text-stone-500"}`}
                >
                  {timing.line}
                </div>
              )}
            </>
          )}
        </div>
      </button>

      {peekOpen && !isOpen && (
        <RoomPeek
          plan={plan} state={state} style={style} timing={timing}
          isAssigned={isAssigned} isPreferred={isPreferredForCurrent}
          side={side} onDismiss={closePeek}
        />
      )}

      {isOpen && (
        <RoomPopover
          room={state} rn={plan.name} mts={mts}
          assignedIds={assignedIds} onClose={onClose}
          onSetTherapist={onSetTherapist} onSetState={onSetState}
          onSetExpectedAt={onSetExpectedAt}
          hideType
        />
      )}
    </div>
  );
}

/**
 * RoomPeek — a quiet, read-only contextual card that appears on hover (desktop)
 * or long-press (mobile). It never blocks the popover — long-press opens peek,
 * a short tap or click still opens the popover. Dismissed on hover-out or by
 * tapping the backdrop on mobile.
 */
function RoomPeek({ plan, state, style, timing, isAssigned, isPreferred, side, onDismiss }) {
  return (
    <>
      {/* Mobile backdrop — dismisses peek on outer tap. Invisible on desktop
          because hover-out already dismisses. */}
      <div className="fixed inset-0 z-30 md:hidden" onClick={onDismiss} aria-hidden="true" />
      <div
        role="tooltip"
        data-testid={`floor-peek-${plan.id}`}
        className={`absolute z-40 top-full mt-2 w-64 rounded-2xl quiet-glass-peek p-4 space-y-2.5 text-left ${
          side === "left" ? "left-0" : "right-0"
        }`}
      >
        {/* Header — room name + type + spatial location */}
        <div className="flex items-baseline justify-between gap-2">
          <div className="min-w-0">
            <div className="font-serif-display text-base text-stone-900 truncate">{plan.name}</div>
            <div className="text-[10px] label-eyebrow text-stone-400 mt-0.5">
              {side === "left" ? "Left" : "Right"} side · Position {plan.position}
              {ROOM_TYPE_LABEL[plan.type] && ` · ${ROOM_TYPE_LABEL[plan.type]}`}
            </div>
          </div>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot}`} aria-hidden="true" />
        </div>

        {/* State line */}
        <div className="text-[11px] label-eyebrow text-stone-500">{style.label}</div>

        {/* Timer if applicable */}
        {timing && (
          <div className={`text-xs ${timing.urgent ? "text-amber-700" : "text-stone-600"}`}>
            {timing.line}
          </div>
        )}

        {/* Assigned therapist details */}
        {isAssigned ? (
          <div className="pt-1 border-t border-stone-100">
            <div className="text-sm text-stone-900">{state.mt_name}</div>
            {state.shift && <div className="text-[11px] text-stone-500 mt-0.5">Shift · {state.shift}</div>}
            {isPreferred && (
              <div className="text-[11px] text-emerald-700 mt-1.5 inline-flex items-center gap-1">
                <Sparkles size={10} strokeWidth={1.5} /> Preferred room
              </div>
            )}
          </div>
        ) : state.state === "available" ? (
          <div className="text-[11px] text-stone-500 italic pt-1 border-t border-stone-100">
            Curtains open · ready to receive a guest.
          </div>
        ) : null}

        {/* v2 placeholder — readiness checklist / recent activity will land here. */}
      </div>
    </>
  );
}

/**
 * Auto-assign v2 — spatial.
 *   1. Skip unavailable rooms (laundry / maintenance / closed) and inactive rooms.
 *   2. Reserve specialty rooms (couples / wet / stretch) until standard rooms fill.
 *   3. Honor therapist preferences.
 *   4. Distribute remaining therapists — keep adjacent rooms occupied before spreading.
 */
function autoAssignRoomsSpatial(mts, prefs, currentRooms, layout) {
  const rooms = { ...(currentRooms || {}) };
  const usedRooms = new Set();
  let unavailableCount = 0;
  const clear = (id) => { const cur = rooms[id] || {}; rooms[id] = { ...cur, state: "available", mt_id: null, mt_name: null, shift: null }; };

  // Prep: honor existing unavailable states; clear existing therapist assignments elsewhere.
  for (const room of layout) {
    const cur = rooms[room.id] || {};
    if (["laundry", "maintenance", "closed"].includes(cur.state)) {
      usedRooms.add(room.id);
      unavailableCount += 1;
    } else {
      clear(room.id);
    }
  }

  // Standard rooms come first; specialty rooms are reserved unless we run out.
  const orderPos = (r) => (r.side === "left" ? 0 : 1000) + r.position;
  const standardRooms  = layout.filter((r) => r.type === "standard" && !usedRooms.has(r.id)).sort((a, b) => orderPos(a) - orderPos(b));
  const specialtyRooms = layout.filter((r) => r.type !== "standard" && !usedRooms.has(r.id)).sort((a, b) => orderPos(a) - orderPos(b));

  let honored = 0;
  const remaining = new Set(mts.map((m) => m.employee_id));

  // Step 3 — preferences (matched against room id list; frontend still passes numeric ids in prefs — support both).
  const roomIdByLegacyNumber = new Map();
  for (const r of layout) {
    // Legacy preferences store room numbers (1-11). Map them to their layout id if names look like "Room N".
    const m = r.name.match(/^Room\s+(\d+)/i);
    if (m) roomIdByLegacyNumber.set(parseInt(m[1], 10), r.id);
  }

  for (const mt of mts) {
    const p = prefs.find((x) => x.employee_id === mt.employee_id);
    if (!p) continue;
    for (const rn of p.preferred_rooms || []) {
      const targetId = roomIdByLegacyNumber.get(rn);
      if (!targetId || usedRooms.has(targetId)) continue;
      const target = layout.find((r) => r.id === targetId);
      if (!target || !target.active) continue;
      rooms[targetId] = { ...(rooms[targetId] || {}), state: "assigned", mt_id: mt.employee_id, mt_name: mt.employee_name, shift: mt.shift };
      usedRooms.add(targetId); remaining.delete(mt.employee_id); honored += 1;
      break;
    }
  }

  // Step 4 — fill standard rooms first, adjacency-friendly (position-ordered).
  let assigned = honored;
  const mtQueue = mts.filter((m) => remaining.has(m.employee_id));
  const rooms_to_fill = [...standardRooms.filter((r) => !usedRooms.has(r.id))];
  while (mtQueue.length && rooms_to_fill.length) {
    const mt = mtQueue.shift();
    const target = rooms_to_fill.shift();
    rooms[target.id] = { ...(rooms[target.id] || {}), state: "assigned", mt_id: mt.employee_id, mt_name: mt.employee_name, shift: mt.shift };
    usedRooms.add(target.id); assigned += 1;
  }
  // If specialty rooms are needed, dip into them.
  let specialtyReserved = specialtyRooms.length;
  const specialty_to_fill = [...specialtyRooms];
  while (mtQueue.length && specialty_to_fill.length) {
    const mt = mtQueue.shift();
    const target = specialty_to_fill.shift();
    rooms[target.id] = { ...(rooms[target.id] || {}), state: "assigned", mt_id: mt.employee_id, mt_name: mt.employee_name, shift: mt.shift };
    usedRooms.add(target.id); assigned += 1; specialtyReserved -= 1;
  }

  return {
    rooms,
    summary: {
      assigned,
      unavailable: unavailableCount,
      preferences_honored: honored,
      specialty_reserved: Math.max(0, specialtyReserved),
      unplaced: mtQueue.length,
    },
  };
}

function RoomPopover({ room, rn, mts, assignedIds, onClose, onSetTherapist, onSetState, onSetType, onSetExpectedAt, hideType = false }) {
  const [type, setType] = useState(room.room_type || "");
  // Escape closes the popover — keyboard-only users need a dismissal path.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const STATES = [
    { key: "assigned",    label: "Assigned"      },
    { key: "available",   label: "Ready"         },
    { key: "turnover",    label: "Turnover"      },
    { key: "laundry",     label: "Laundry"       },
    { key: "maintenance", label: "Maintenance"   },
    { key: "closed",      label: "Closed"        },
  ];
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-label={`${rn} options`}
        data-testid={`room-popover-${rn}`}
        // Defensive: any pointer/mouse/click event that happens INSIDE the
        // popover must not bubble to the tile button or the backdrop overlay.
        // Also swallow pointerdown so a native <select>'s mousedown can't be
        // interpreted as an outside-click by an ancestor listener.
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className="absolute z-50 mt-2 left-0 right-0 sm:left-auto sm:right-0 sm:w-72 bg-white rounded-2xl subtle-border shadow-xl p-4 space-y-3"
      >
        <div className="flex items-center justify-between">
          <div className="font-serif-display text-lg text-stone-900">{rn}</div>
          <button onClick={onClose} data-testid={`room-close-${rn}`} className="text-stone-400 hover:text-stone-700" aria-label="Close"><X size={14} /></button>
        </div>

        <div className="grid grid-cols-2 gap-1.5">
          {STATES.map((s) => (
            <button
              key={s.key}
              onClick={() => onSetState(s.key)}
              data-testid={`room-state-${rn}-${s.key}`}
              className={`text-left px-3 py-1.5 rounded-lg text-xs ${room.state === s.key ? "bg-stone-900 text-white" : "bg-stone-50 text-stone-700 hover:bg-stone-100"}`}
            >
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle ${ROOM_STATE_STYLES[s.key].dot}`} />
              {s.label}
            </button>
          ))}
        </div>

        {room.state === "assigned" && (
          <div>
            <div className="text-[10px] label-eyebrow text-stone-400 mb-1">Assign therapist</div>
            <select
              value={room.mt_id || ""}
              onChange={(e) => onSetTherapist(e.target.value)}
              data-testid={`room-select-${rn}`}
              className="w-full text-sm px-3 py-2 rounded-lg subtle-border outline-none"
            >
              <option value="">— pick a therapist —</option>
              {mts.map((mt) => {
                const taken = assignedIds.has(mt.employee_id) && room.mt_id !== mt.employee_id;
                return (
                  <option key={mt.employee_id} value={mt.employee_id} disabled={taken}>
                    {mt.employee_name}{taken ? " (assigned)" : ""}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        {(room.state === "laundry" || room.state === "turnover") && (
          <div>
            <div className="text-[10px] label-eyebrow text-stone-400 mb-1">
              Ready around <span className="text-stone-300 normal-case tracking-normal">· optional</span>
            </div>
            <input
              type="time"
              value={room.expected_at ? isoToTimeLocal(room.expected_at) : ""}
              onChange={(e) => onSetExpectedAt && onSetExpectedAt(timeLocalToIso(e.target.value))}
              data-testid={`room-expected-${rn}`}
              className="w-full text-sm px-3 py-2 rounded-lg subtle-border outline-none"
            />
          </div>
        )}

        {!hideType && (
        <div>
          <div className="text-[10px] label-eyebrow text-stone-400 mb-1">Room type <span className="text-stone-300 normal-case tracking-normal">· optional</span></div>
          <input
            value={type}
            onChange={(e) => setType(e.target.value)}
            onBlur={() => type !== (room.room_type || "") && onSetType && onSetType(type)}
            placeholder="e.g. Couples, Wet Room, Stretch"
            data-testid={`room-type-${rn}`}
            className="w-full text-sm px-3 py-2 rounded-lg subtle-border outline-none"
          />
        </div>
        )}
      </div>
    </>
  );
}

// ------------- Page -------------
export default function Daily() {
  const companion = useCompanion();
  const { user } = useAuth();
  const [date, setDate] = useState(iso(new Date()));
  const [boutique, setBoutique] = useState(user?.boutique_code || "PLT");
  const [log, setLog] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [prefs, setPrefs] = useState([]);
  const [floorPlan, setFloorPlan] = useState(null);

  const canEditAll = user && (user.role === "owner" || user.role === "recruiter" || (user.role === "boutique_manager" && user.boutique_code === boutique));

  const load = () => {
    api.get(`/daily-logs/${boutique}/${date}`).then((r) => { setLog(r.data); setDirty(false); });
    api.get("/journey-rooms/preferences", { params: { boutique } }).then((r) => setPrefs(r.data || [])).catch(() => setPrefs([]));
    api.get(`/boutique-config/${boutique}/floor-plan`).then((r) => setFloorPlan(r.data)).catch(() => setFloorPlan(null));
  };
  useEffect(() => { load(); }, [boutique, date]); // eslint-disable-line

  const updateRow = (empId, patch) => {
    setLog((prev) => ({
      ...prev,
      roster: prev.roster.map((r) => {
        if (r.employee_id !== empId) return r;
        const nxt = { ...r, ...patch };
        // If GM edits the shift, mark as manual override
        if ("shift" in patch) {
          nxt.shift_override = patch.shift !== "" && patch.shift !== (r.shift_source === "zenoti" ? r.shift : "");
          if (patch.shift && patch.shift !== r.shift) nxt.shift_source = "manual";
          if (!patch.shift) { nxt.shift_source = ""; nxt.shift_override = false; }
        }
        return nxt;
      }),
    }));
    setDirty(true);
  };
  const updateDuty = (id, patch) => {
    setLog((prev) => ({
      ...prev,
      side_duties: (prev.side_duties || []).map((d) => (d.id === id ? { ...d, ...patch } : d)),
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/daily-logs", {
        boutique_code: boutique, date,
        roster: log.roster, rooms: log.rooms || {},
        side_duties: log.side_duties, notes: log.notes || "",
        gm_agenda: log.gm_agenda || [],
        pending_gratuities: log.pending_gratuities || [],
      });
      setLog(r.data);
      setDirty(false);
      toast.success("Log saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const shift = (delta) => {
    const d = new Date(date); d.setDate(d.getDate() + delta); setDate(iso(d));
  };

  if (!log) return (
    <div>
      <Topbar title="Today's Journey" context="Preparing the people and rooms carrying today’s guest experience." />
      <div className="px-4 sm:px-10 pb-12 sm:pb-16 space-y-4" data-testid="daily-skeleton">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-14 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    </div>
  );

  const canEditRow = (empId) => canEditAll || empId === user?.user_id;
  const mts = (log.roster || []).filter((r) => r.role === "massage_therapist");
  const zen = log.zenoti_import || {};

  // Only teammates working today — shift set and not "OFF".
  const workingRoster = (log.roster || []).filter((r) => {
    const s = (r.shift || "").trim();
    return s && s.toUpperCase() !== "OFF";
  });
  // Front-of-house (EGs + GM) vs. therapists — displayed in separate sections.
  const teamWorking = workingRoster.filter((r) => r.role === "front_desk" || r.role === "boutique_manager");
  const mtsWorking  = workingRoster.filter((r) => r.role === "massage_therapist");
  const meRow = workingRoster.find((r) => r.employee_id === user?.user_id);

  return (
    <div>
      <Topbar
        title="Today's Journey"
        context={`${fmtDate(new Date(date))} · ${boutiqueOf(boutique).short}`}
        attention={!zen.found ? {
          tone: "attention",
          label: "Zenoti schedule still needs import",
          detail: "Bring in the current schedule before assigning rooms.",
        } : null}
        status={zen.found ? { tone: "ready", label: "Zenoti schedule connected" } : null}
        primaryAction={canEditAll ? (
          <button onClick={() => setImportOpen(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/25 bg-white/10 text-sm text-white hover:bg-white/20">
            <Upload size={14} strokeWidth={1.5} /> {zen.found ? "Refresh schedule" : "Import schedule"}
          </button>
        ) : null}
      />
      <div className="px-4 sm:px-10 pb-12 sm:pb-16 space-y-6 stagger-fade">
        <section className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <button onClick={() => shift(-1)} data-testid="day-prev" className="p-2 rounded-full hover:bg-stone-100"><ChevronLeft size={16} strokeWidth={1.25} /></button>
            <div className="font-serif-display text-2xl text-stone-900">{fmtDate(new Date(date))}</div>
            <button onClick={() => shift(1)} data-testid="day-next" className="p-2 rounded-full hover:bg-stone-100"><ChevronRight size={16} strokeWidth={1.25} /></button>
          </div>
          <div className="flex items-center gap-3">
            <select value={boutique} onChange={(e) => setBoutique(e.target.value)} data-testid="daily-boutique" className="px-4 py-2 rounded-full subtle-border text-sm outline-none">
              {["PLT", "FLL", "WYN", "PNC"].map((b) => <option key={b} value={b}>{boutiqueOf(b).short}</option>)}
            </select>
            {canEditAll && (
              <button onClick={() => setImportOpen(true)} data-testid="open-zenoti-import" className="flex items-center gap-1.5 px-4 py-2 rounded-full subtle-border text-sm text-stone-700 hover:bg-stone-50" title="Import Zenoti schedule">
                <Upload size={14} strokeWidth={1.5} className="text-stone-500" /> Import
              </button>
            )}
            {canEditAll && (
              <button onClick={() => setPrefsOpen(true)} className="p-2 rounded-full hover:bg-stone-100" title="Room preferences" data-testid="open-prefs">
                <Settings size={16} strokeWidth={1.25} className="text-stone-500" />
              </button>
            )}
            <button onClick={save} disabled={!dirty || saving} data-testid="daily-save" className="flex items-center gap-2 px-5 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-40">
              <Save size={14} strokeWidth={1.5} /> {saving ? "Saving…" : dirty ? "Save" : "Saved"}
            </button>
          </div>
        </section>

        {/* Zenoti source banner */}
        <section className={`rounded-2xl border px-5 py-3 text-xs flex items-center justify-between gap-3 ${zen.found ? "border-emerald-100 bg-emerald-50/60 text-emerald-800" : "border-amber-100 bg-amber-50/60 text-amber-800"}`} data-testid="zenoti-banner">
          {zen.found ? (
            <span>Shifts pre-filled from Zenoti · week of {zen.week_start} · {zen.source_filename || "upload"}{zen.imported_by_name ? ` · by ${zen.imported_by_name}` : ""}</span>
          ) : (
            <span>No Zenoti schedule imported for this week yet.</span>
          )}
          {canEditAll && (
            <button onClick={() => setImportOpen(true)} data-testid="banner-import" className="flex items-center gap-1 px-3 py-1 rounded-full bg-white/70 hover:bg-white text-[11px] whitespace-nowrap">
              <Upload size={11} strokeWidth={1.5} /> {zen.found ? "Update" : "Import now"}
            </button>
          )}
        </section>

        {/* Personal Today card — role-tailored */}
        {meRow && (
          <PersonalCard
            row={meRow}
            log={log}
            boutique={boutique}
            date={date}
            user={user}
            onGratuityClaimed={load}
            onRoomResetLogged={load}
            onAgendaChange={(nxt) => { setLog((p) => ({ ...p, gm_agenda: nxt })); setDirty(true); }}
            onGratuityAdded={load}
            canEditAll={canEditAll}
            workingRoster={workingRoster}
          />
        )}

        {/* Today's Journey — the people walking alongside our guests today (EG + GM). */}
        <section className="bg-white subtle-border rounded-2xl overflow-hidden card-shadow" data-testid="daily-team">
          <div className="px-6 py-3 border-b border-stone-100 flex items-center justify-between">
            <div>
              <div className="font-serif-display text-lg text-stone-900">Today's Journey</div>
              <div className="text-[11px] text-stone-400">Who's holding the space today.</div>
            </div>
            <div className="text-[10px] label-eyebrow text-stone-400">
              {teamWorking.length} on shift
            </div>
          </div>
          {teamWorking.length === 0 ? (
            <div className="p-8 text-sm text-stone-400 italic">Nobody's rostered for the front-of-house today.</div>
          ) : (
            <ul className="divide-y divide-stone-50">
              {teamWorking.map((r) => {
                const editable = canEditRow(r.employee_id);
                const mine = r.employee_id === user?.user_id;
                const fromZen = r.shift_source === "zenoti" && !!r.shift;
                return (
                  <li key={r.employee_id} className={`px-6 py-3 flex items-center gap-3 ${mine ? "bg-stone-50/70" : ""}`} data-testid={`team-row-${r.employee_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-stone-900 truncate">{r.employee_name}</div>
                      <div className="text-[10px] text-stone-400">{r.role === "boutique_manager" ? "GM" : "Experience Guide"}</div>
                    </div>
                    <div className="w-32 shrink-0">
                      <input
                        value={r.shift || ""}
                        onChange={(e) => updateRow(r.employee_id, { shift: e.target.value })}
                        disabled={!canEditAll}
                        placeholder="—"
                        data-testid={`shift-${r.employee_id}`}
                        className="w-full px-3 py-1.5 rounded-lg text-xs outline-none border border-transparent focus:border-stone-200 focus:bg-stone-50 disabled:opacity-70 text-stone-700 placeholder:text-stone-300"
                      />
                      {fromZen && (
                        <div className="text-[9px] text-emerald-700 mt-0.5 flex items-center gap-1">
                          <Sparkles size={8} strokeWidth={2} /> from Zenoti
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {CHECK_KEYS.map((c) => (
                        <button
                          key={c.k}
                          onClick={() => editable && updateRow(r.employee_id, { [c.k]: !r[c.k] })}
                          disabled={!editable}
                          data-testid={`chk-${r.employee_id}-${c.k}`}
                          title={c.label}
                          aria-label={c.label}
                          className={`w-7 h-7 rounded-full border transition-colors flex items-center justify-center ${r[c.k] ? "bg-stone-900 border-stone-900" : "border-stone-200 hover:border-stone-400"} disabled:opacity-40`}
                        >
                          {r[c.k] && <Check size={12} strokeWidth={2} className="text-white" />}
                        </button>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Journey Rooms — a spatial map of the boutique. */}
        <BoutiqueFloor log={log} prefs={prefs} floorPlan={floorPlan} boutique={boutique} onChange={(nxt) => { setLog(nxt); setDirty(true); }} canEditAll={canEditAll} />

        {/* Today's Side Duties — shared work, chip-based ownership. */}
        <SideDuties log={log} onChange={(nxt) => { setLog(nxt); setDirty(true); }} canEditAll={canEditAll} user={user} workingRoster={workingRoster} />

        {/* Massage Therapists — compact summary; the primary view is the room grid above. */}
        {mtsWorking.length > 0 && (
          <section className="bg-white subtle-border rounded-2xl overflow-hidden card-shadow" data-testid="daily-mts">
            <div className="px-6 py-3 border-b border-stone-100">
              <div className="font-serif-display text-lg text-stone-900">Massage Therapists</div>
              <div className="text-[11px] text-stone-400">Represented in the room grid above.</div>
            </div>
            <ul className="divide-y divide-stone-50">
              {mtsWorking.map((r) => {
                const roomEntry = Object.entries(log.rooms || {}).find(([, v]) => v?.mt_id === r.employee_id);
                const roomId = roomEntry?.[0];
                const plan = (floorPlan?.layout || []).find((p) => p.id === roomId);
                const label = plan ? plan.name : roomId ? `Room ${roomId}` : null;
                return (
                  <li key={r.employee_id} className="px-6 py-2.5 flex items-center gap-3 text-sm" data-testid={`mt-row-${r.employee_id}`}>
                    <div className="flex-1 min-w-0 truncate text-stone-800">{r.employee_name}</div>
                    <div className="text-[11px] text-stone-500 shrink-0 tabular-nums">{r.shift || "—"}</div>
                    <div className="w-24 text-right text-[11px] shrink-0">
                      {label ? <span className="text-stone-800">{label}</span> : <span className="text-stone-300">Unassigned</span>}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* Notes */}
        <section className="bg-white subtle-border rounded-2xl p-6 card-shadow">
          <div className="label-eyebrow mb-2">Notes for the team</div>
          <textarea value={log.notes || ""} onChange={(e) => { setLog((p) => ({ ...p, notes: e.target.value })); setDirty(true); }} disabled={!canEditAll} rows={3} placeholder="What should the team know about today?" className="w-full px-4 py-3 rounded-2xl subtle-border text-sm outline-none focus:border-stone-400 disabled:bg-stone-50" />
        </section>
      </div>

      <PrefsDialog open={prefsOpen} onClose={() => setPrefsOpen(false)} boutique={boutique} mts={mts} />
      <ZenotiImportDialog open={importOpen} onClose={() => setImportOpen(false)} boutique={boutique} onImported={load} />
    </div>
  );
}


// ─────────────── Side Duties · shared work with chip ownership ───────────────
function SideDuties({ log, onChange, canEditAll, user, workingRoster }) {
  const duties = log.side_duties || [];
  const [addingFor, setAddingFor] = useState(null); // duty id
  const [pickerFor, setPickerFor] = useState(null); // duty id currently picking an owner
  const [newDutyLabel, setNewDutyLabel] = useState("");

  const editDuty = (id, patch) => {
    onChange({
      ...log,
      side_duties: duties.map((d) => (d.id === id ? { ...d, ...patch } : d)),
    });
  };

  const setOwners = (id, owners) => editDuty(id, { owners });

  const toggleOwnerDone = (dutyId, ownerIdx) => {
    const d = duties.find((x) => x.id === dutyId);
    if (!d) return;
    const owners = d.owners.map((o, i) =>
      i !== ownerIdx ? o : { ...o, done_at: o.done_at ? null : new Date().toISOString() }
    );
    setOwners(dutyId, owners);
  };

  const addOwner = (dutyId, emp) => {
    const d = duties.find((x) => x.id === dutyId);
    if (!d) return;
    if (d.owners.some((o) => o.user_id === emp.employee_id)) { setPickerFor(null); return; }
    setOwners(dutyId, [...d.owners, { user_id: emp.employee_id, user_name: emp.employee_name, done_at: null }]);
    setPickerFor(null);
  };

  const removeOwner = (dutyId, ownerIdx) => {
    const d = duties.find((x) => x.id === dutyId);
    if (!d) return;
    setOwners(dutyId, d.owners.filter((_, i) => i !== ownerIdx));
  };

  const addDuty = () => {
    const label = newDutyLabel.trim();
    if (!label) return;
    const id = `sd_${Math.random().toString(36).slice(2, 10)}`;
    onChange({ ...log, side_duties: [...duties, { id, label, owners: [] }] });
    setNewDutyLabel("");
    setAddingFor(null);
  };

  const removeDuty = (id) => onChange({ ...log, side_duties: duties.filter((d) => d.id !== id) });

  return (
    <section className="bg-white subtle-border rounded-2xl overflow-hidden card-shadow" data-testid="daily-duties">
      <div className="px-6 py-3 border-b border-stone-100 flex items-center justify-between">
        <div>
          <div className="font-serif-display text-lg text-stone-900">Today's Side Duties</div>
          <div className="text-[11px] text-stone-400">Who owns today's shared work.</div>
        </div>
        {canEditAll && (
          <button
            onClick={() => setAddingFor("new")}
            data-testid="duty-add-new"
            className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-stone-50 hover:bg-stone-100 text-stone-700"
          >
            <Plus size={11} strokeWidth={1.5} /> Add duty
          </button>
        )}
      </div>

      {addingFor === "new" && (
        <div className="px-6 pt-4 flex items-center gap-2" data-testid="duty-new-form">
          <input
            value={newDutyLabel}
            onChange={(e) => setNewDutyLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addDuty()}
            placeholder="e.g. Deep clean Room 8"
            data-testid="duty-new-label"
            className="flex-1 px-3 py-1.5 rounded-lg subtle-border text-sm outline-none"
            autoFocus
          />
          <button onClick={addDuty} className="text-xs px-3 py-1.5 rounded-full bg-stone-900 text-white">Add</button>
          <button onClick={() => { setAddingFor(null); setNewDutyLabel(""); }} className="text-xs text-stone-500">Cancel</button>
        </div>
      )}

      {duties.length === 0 ? (
        <div className="p-8 text-sm text-stone-400 italic">No duties yet today.</div>
      ) : (
        <ul className="divide-y divide-stone-50">
          {duties.map((d) => {
            const allDone = d.owners.length > 0 && d.owners.every((o) => o.done_at);
            const myIdx = d.owners.findIndex((o) => o.user_id === user?.user_id);
            const canToggleSelf = myIdx >= 0;
            return (
              <li key={d.id} className="px-6 py-4 flex items-start gap-4" data-testid={`duty-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${allDone ? "text-stone-400 line-through" : "text-stone-900"}`}>{d.label}</div>
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    {d.owners.length === 0 && (
                      <span className="text-[11px] text-stone-400 italic">Unassigned</span>
                    )}
                    {d.owners.map((o, i) => (
                      <OwnerChip
                        key={i}
                        owner={o}
                        canToggle={canEditAll || o.user_id === user?.user_id}
                        canRemove={canEditAll}
                        onToggle={() => toggleOwnerDone(d.id, i)}
                        onRemove={() => removeOwner(d.id, i)}
                        testId={`duty-${d.id}-owner-${i}`}
                      />
                    ))}
                    {canEditAll && (
                      <div className="relative">
                        <button
                          onClick={() => setPickerFor(pickerFor === d.id ? null : d.id)}
                          data-testid={`duty-${d.id}-add-owner`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-dashed border-stone-300 text-[11px] text-stone-500 hover:bg-stone-50"
                        >
                          <Plus size={10} strokeWidth={1.5} />
                        </button>
                        {pickerFor === d.id && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setPickerFor(null)} aria-hidden="true" />
                            <div className="absolute z-50 mt-1 left-0 w-52 bg-white rounded-xl subtle-border shadow-xl p-2 max-h-64 overflow-y-auto" data-testid={`duty-${d.id}-picker`}>
                              {workingRoster.length === 0 && <div className="text-[11px] text-stone-400 p-2">No one on shift.</div>}
                              {workingRoster.map((emp) => (
                                <button
                                  key={emp.employee_id}
                                  onClick={() => addOwner(d.id, emp)}
                                  data-testid={`duty-${d.id}-pick-${emp.employee_id}`}
                                  className="w-full text-left px-2 py-1.5 text-xs rounded-md hover:bg-stone-50 text-stone-700"
                                >
                                  {emp.employee_name}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {canToggleSelf && !canEditAll && (
                      <span className="text-[10px] text-stone-400 ml-1">Tap your name to check it off</span>
                    )}
                  </div>
                </div>
                {canEditAll && (
                  <button
                    onClick={() => removeDuty(d.id)}
                    data-testid={`duty-${d.id}-remove`}
                    className="shrink-0 text-stone-300 hover:text-rose-600 p-1"
                    aria-label={`Remove duty ${d.label}`}
                  >
                    <Trash2 size={13} strokeWidth={1.25} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function OwnerChip({ owner, canToggle, canRemove, onToggle, onRemove, testId }) {
  const done = !!owner.done_at;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full text-[11px] transition-colors ${
        done ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-700"
      }`}
    >
      <button
        type="button"
        onClick={canToggle ? onToggle : undefined}
        disabled={!canToggle}
        className={`inline-flex items-center justify-center w-4 h-4 rounded-full transition-colors ${
          done ? "bg-emerald-500 text-white" : "border border-stone-300 hover:border-stone-500"
        } disabled:cursor-default`}
        aria-label={done ? `Undo ${owner.user_name}'s completion` : `Mark done for ${owner.user_name}`}
        data-testid={`${testId}-toggle`}
      >
        {done && <Check size={9} strokeWidth={2.5} />}
      </button>
      <span className={done ? "line-through" : ""}>{owner.user_name}</span>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-stone-400 hover:text-stone-700"
          aria-label={`Remove ${owner.user_name}`}
          data-testid={`${testId}-remove`}
        >
          <X size={10} strokeWidth={1.5} />
        </button>
      )}
    </span>
  );
}

// ─────────────────────────── Personal role-tailored card ───────────────────────────
function PersonalCard({ row, log, boutique, date, user, onGratuityClaimed, onRoomResetLogged, onAgendaChange, onGratuityAdded, canEditAll, workingRoster }) {
  const role = row.role;
  const isMT = role === "massage_therapist";
  const isGM = role === "boutique_manager";
  const isEG = role === "front_desk";

  return (
    <section className="bg-white subtle-border rounded-2xl overflow-hidden card-shadow" data-testid="personal-card">
      <div className="px-6 py-4 border-b border-stone-100 flex items-baseline gap-3">
        <div className="label-eyebrow text-stone-500">Your day</div>
        <div className="font-serif-display text-lg text-stone-900 flex-1">{row.employee_name}</div>
        <div className="text-xs text-stone-500">{row.shift || "—"}</div>
      </div>
      <div className="p-6">
        {isMT && <MTPersonalCard row={row} log={log} boutique={boutique} date={date} user={user} onGratuityClaimed={onGratuityClaimed} onRoomResetLogged={onRoomResetLogged} />}
        {isGM && <GMPersonalCard log={log} boutique={boutique} date={date} onAgendaChange={onAgendaChange} onGratuityAdded={onGratuityAdded} workingRoster={workingRoster} canEditAll={canEditAll} />}
        {isEG && <EGPersonalCard row={row} log={log} workingRoster={workingRoster} />}
        {!isMT && !isGM && !isEG && (
          <p className="text-sm text-stone-500">Welcome — here's the team's shape for today.</p>
        )}
        {(isMT || isEG) && <PersonalJourneyLink role={role} />}
      </div>
    </section>
  );
}

function PersonalJourneyLink({ role }) {
  const isMT = role === "massage_therapist";
  const title = isMT ? "Your room actions" : "Your Experience Guide schedule";
  const detail = isMT ? "Room status, shift-close checklist, and flag-room support." : "Call Button, Clean Sweep, and your daily checklists.";
  return <section className="mt-4 flex items-center gap-4 rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3"><span className="min-w-0 flex-1"><span className="block text-[10px] label-eyebrow text-stone-500">Today’s Journey</span><strong className="mt-0.5 block text-sm text-stone-900">{title}</strong><span className="mt-1 block text-xs leading-5 text-stone-500">{detail}</span></span><OpenButton tone="light" aria-label="Open Today’s Journey" to="/operations/journey-planning" data-testid={`open-${isMT ? "mt" : "eg"}-journey`}>Open</OpenButton></section>;
}

// ─── MT personal card ───
function MTPersonalCard({ row, log, boutique, date, user, onGratuityClaimed, onRoomResetLogged }) {
  const rooms = log.rooms || {};
  const myRoom = Object.entries(rooms).find(([, v]) => v?.mt_id === row.employee_id);
  const roomNumber = myRoom?.[0];
  const mySideDuties = (log.side_duties || []).filter((d) => (d.assigned_to || "").toLowerCase().includes((row.employee_name || "").split(" ")[0].toLowerCase()));
  const myGrats = (log.pending_gratuities || []).filter((g) => g.mt_id === row.employee_id && !g.claimed_at);
  const [resetOpen, setResetOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MiniStat label="Room" value={roomNumber ? `#${roomNumber}` : "—"} icon={DoorOpen} testid="mt-room" />
        <MiniStat label="Side duty" value={mySideDuties.length > 0 ? mySideDuties[0].label : "—"} testid="mt-duty" small />
        <MiniStat label="Shift" value={row.shift || "—"} testid="mt-shift" />
      </div>

      <button
        onClick={() => setResetOpen(true)}
        disabled={!roomNumber}
        data-testid="mt-reset-room"
        className="w-full px-4 py-3 rounded-2xl bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <Sparkles size={14} strokeWidth={1.5} /> {roomNumber ? `Reset Room ${roomNumber} — Journey Room Standards` : "Room not assigned yet"}
      </button>

      {myGrats.length > 0 && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 space-y-3" data-testid="mt-gratuities">
          <div className="flex items-center gap-2">
            <Hand size={14} strokeWidth={1.5} className="text-emerald-700" />
            <div className="font-serif-display text-emerald-900 text-base">Cash gratuity waiting at the front desk</div>
          </div>
          <ul className="space-y-2">
            {myGrats.map((g) => (
              <li key={g.id} className="flex items-center gap-3 text-sm text-emerald-900" data-testid={`mt-grat-${g.id}`}>
                <span className="font-medium tabular-nums">${g.amount.toFixed(2)}</span>
                {g.note && <span className="text-emerald-700 text-xs italic truncate">— {g.note}</span>}
                <button
                  onClick={async () => {
                    await api.post(`/daily-logs/${boutique}/${date}/gratuities/${g.id}/claim`);
                    toast.success("Claimed — enjoy!");
                    celebrateSoft({ origin: { x: 0.5, y: 0.75 }, particleCount: 45 });
                    onGratuityClaimed();
                  }}
                  data-testid={`mt-claim-${g.id}`}
                  className="ml-auto text-xs px-3 py-1 rounded-full bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                >
                  Claim
                </button>
              </li>
            ))}
          </ul>
          <div className="text-xs text-emerald-800/80 italic pt-1 border-t border-emerald-200">
            Gentle reminder: never hand off a gratuity in front of a guest. Please claim it privately before you leave for the day.
          </div>
        </div>
      )}

      {resetOpen && (
        <RoomResetDialog
          room={roomNumber}
          boutique={boutique}
          date={date}
          onClose={() => setResetOpen(false)}
          onLogged={() => { setResetOpen(false); onRoomResetLogged(); }}
        />
      )}
    </div>
  );
}

// ─── GM personal card ───
function GMPersonalCard({ log, boutique, date, onAgendaChange, onGratuityAdded, workingRoster, canEditAll }) {
  const agenda = log.gm_agenda || [];
  const [showAddGrat, setShowAddGrat] = useState(false);
  const addAgenda = () => onAgendaChange([...agenda, { id: uid(), time: "", title: "", contact: "", notes: "" }]);
  const setField = (idx, field, val) => onAgendaChange(agenda.map((a, i) => i === idx ? { ...a, [field]: val } : a));
  const remove = (idx) => onAgendaChange(agenda.filter((_, i) => i !== idx));

  const mts = workingRoster.filter((r) => r.role === "massage_therapist");

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Calendar size={13} strokeWidth={1.5} className="text-stone-500" />
          <div className="text-sm font-serif-display text-stone-900">Front desk should field for you</div>
          <button onClick={addAgenda} data-testid="gm-agenda-add" className="ml-auto text-xs px-3 py-1 rounded-full bg-stone-100 hover:bg-stone-200 inline-flex items-center gap-1"><Plus size={11} strokeWidth={2} /> Add</button>
        </div>
        {agenda.length === 0 ? (
          <div className="text-xs text-stone-400 italic">Nothing on the docket. Add vendor calls, deliveries, interviews, corporate touchpoints so the front desk knows how to route inbound calls.</div>
        ) : (
          <ul className="space-y-2" data-testid="gm-agenda-list">
            {agenda.map((a, i) => (
              <li key={a.id || i} className="grid grid-cols-12 gap-2 items-center">
                <input value={a.time || ""} onChange={(e) => setField(i, "time", e.target.value)} data-testid={`gm-agenda-time-${i}`} placeholder="10am" className="col-span-2 px-2 py-1.5 rounded-lg subtle-border text-xs outline-none focus:border-stone-400" />
                <input value={a.title || ""} onChange={(e) => setField(i, "title", e.target.value)} data-testid={`gm-agenda-title-${i}`} placeholder="e.g. Massage Depot delivery" className="col-span-4 px-2 py-1.5 rounded-lg subtle-border text-xs outline-none focus:border-stone-400" />
                <input value={a.contact || ""} onChange={(e) => setField(i, "contact", e.target.value)} data-testid={`gm-agenda-contact-${i}`} placeholder="who's stopping in" className="col-span-3 px-2 py-1.5 rounded-lg subtle-border text-xs outline-none focus:border-stone-400" />
                <input value={a.notes || ""} onChange={(e) => setField(i, "notes", e.target.value)} data-testid={`gm-agenda-notes-${i}`} placeholder="how to route" className="col-span-2 px-2 py-1.5 rounded-lg subtle-border text-xs outline-none focus:border-stone-400" />
                <button onClick={() => remove(i)} data-testid={`gm-agenda-del-${i}`} className="col-span-1 p-1 text-stone-400 hover:text-rose-500"><Trash2 size={12} strokeWidth={1.5} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="pt-4 border-t border-stone-100">
        <div className="flex items-center gap-2 mb-2">
          <Hand size={13} strokeWidth={1.5} className="text-stone-500" />
          <div className="text-sm font-serif-display text-stone-900">Pending cash gratuities</div>
          {canEditAll && (
            <button onClick={() => setShowAddGrat(true)} data-testid="gm-grat-add" className="ml-auto text-xs px-3 py-1 rounded-full bg-stone-100 hover:bg-stone-200 inline-flex items-center gap-1"><Plus size={11} strokeWidth={2} /> Add</button>
          )}
        </div>
        <PendingGratuitiesList log={log} boutique={boutique} date={date} onChange={onGratuityAdded} canEditAll={canEditAll} />
      </div>

      {showAddGrat && (
        <AddGratuityDialog
          mts={mts}
          boutique={boutique}
          date={date}
          onClose={() => setShowAddGrat(false)}
          onAdded={() => { setShowAddGrat(false); onGratuityAdded(); }}
        />
      )}
    </div>
  );
}

// ─── EG personal card (current roster — light-touch for now) ───
function EGPersonalCard({ row, log, workingRoster }) {
  const grats = (log.pending_gratuities || []).filter((g) => !g.claimed_at);
  return (
    <div className="space-y-3 text-sm text-stone-700">
      <p>You're on shift for <span className="font-medium">{row.shift}</span>. The roster below shows everyone working today.</p>
      {grats.length > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          <div className="font-medium mb-1">Front desk: {grats.length} cash gratuit{grats.length === 1 ? "y" : "ies"} in the drawer.</div>
          Reminder — hand these off privately, never in front of a guest.
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, testid, small }) {
  return (
    <div className="rounded-2xl bg-stone-50 p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] label-eyebrow text-stone-500">
        {Icon && <Icon size={11} strokeWidth={1.5} />}
        {label}
      </div>
      <div className={`mt-1 text-stone-900 tabular-nums ${small ? "text-sm" : "text-2xl font-serif-display"}`}>{value}</div>
    </div>
  );
}

function uid() { return Math.random().toString(36).slice(2, 10); }

// ─── Pending gratuities list (shared) ───
function PendingGratuitiesList({ log, boutique, date, onChange, canEditAll }) {
  const items = log.pending_gratuities || [];
  if (items.length === 0) return <div className="text-xs text-stone-400 italic">No cash gratuities currently at the front desk.</div>;
  return (
    <ul className="space-y-1" data-testid="grat-list">
      {items.map((g) => (
        <li key={g.id} className="flex items-center gap-3 text-sm">
          <span className={`w-1.5 h-1.5 rounded-full ${g.claimed_at ? "bg-stone-300" : "bg-emerald-500"}`} />
          <span className="text-stone-800 truncate flex-1">{g.mt_name}</span>
          <span className="text-stone-900 tabular-nums font-medium">${g.amount.toFixed(2)}</span>
          {g.claimed_at ? (
            <span className="text-[10px] text-stone-400">claimed {g.claimed_at.slice(11, 16)}</span>
          ) : (
            canEditAll && (
              <button
                onClick={async () => {
                  await api.post(`/daily-logs/${boutique}/${date}/gratuities/${g.id}/claim`);
                  toast.success(`Marked claimed for ${g.mt_name}`);
                  onChange();
                }}
                data-testid={`grat-claim-${g.id}`}
                className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 hover:bg-stone-200"
              >
                Mark claimed
              </button>
            )
          )}
        </li>
      ))}
    </ul>
  );
}

// ─── Room Reset checklist dialog ───
function RoomResetDialog({ room, boutique, date, onClose, onLogged }) {
  const [items, setItems] = useState([]);
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/daily-logs/journey-checklist").then((r) => setItems(r.data.items || []));
  }, []);
  const allDone = items.length > 0 && items.every((it) => checks[it.key]);
  const done = items.filter((it) => checks[it.key]).length;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/daily-logs/${boutique}/${date}/room-reset`, {
        boutique_code: boutique, date, room: Number(room), checklist: checks,
      });
      toast.success(`Room ${room} reset — thank you!`);
      onLogged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 mobile-modal-wrap" onClick={onClose} data-testid="room-reset-dialog">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl mobile-modal-card" onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-4 border-b border-stone-100 flex items-center">
          <div>
            <div className="text-[10px] label-eyebrow text-stone-400">Journey Room Standards</div>
            <div className="font-serif-display text-lg text-stone-900">Reset Room {room}</div>
          </div>
          <button onClick={onClose} className="ml-auto p-1 text-stone-400 hover:text-stone-800"><X size={16} strokeWidth={1.5} /></button>
        </header>
        <div className="p-5 max-h-[60vh] overflow-y-auto space-y-2">
          {items.map((it) => (
            <label key={it.key} className="flex items-center gap-3 text-sm text-stone-700 cursor-pointer" data-testid={`reset-chk-${it.key}`}>
              <input
                type="checkbox"
                checked={!!checks[it.key]}
                onChange={(e) => setChecks((s) => ({ ...s, [it.key]: e.target.checked }))}
                className="w-4 h-4"
              />
              <span className={checks[it.key] ? "line-through text-stone-400" : ""}>{it.label}</span>
            </label>
          ))}
        </div>
        <footer className="px-5 py-3 border-t border-stone-100 bg-stone-50 rounded-b-2xl flex items-center">
          <div className="text-xs text-stone-500">{done} / {items.length} complete</div>
          <button
            onClick={submit}
            disabled={busy || done === 0}
            data-testid="reset-submit"
            className="ml-auto px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-40"
          >
            {busy ? "Saving…" : allDone ? "Log complete reset" : `Log ${done} step${done === 1 ? "" : "s"}`}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ─── Add-gratuity dialog (GM/Owner) ───
function AddGratuityDialog({ mts, boutique, date, onClose, onAdded }) {
  const [mt, setMt] = useState(mts[0]?.employee_id || "");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!mt) return toast.error("Pick an MT");
    if (!amount || Number(amount) <= 0) return toast.error("Amount is required");
    setBusy(true);
    try {
      await api.post(`/daily-logs/${boutique}/${date}/gratuities`, {
        boutique_code: boutique, date, mt_id: mt, amount: Number(amount), note,
      });
      toast.success("Gratuity logged — MT will see it on their Today view");
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 mobile-modal-wrap" onClick={onClose} data-testid="add-grat-dialog">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl mobile-modal-card" onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-4 border-b border-stone-100 flex items-center">
          <div>
            <div className="text-[10px] label-eyebrow text-stone-400">Cash left at the front desk</div>
            <div className="font-serif-display text-lg text-stone-900">Hold a gratuity</div>
          </div>
          <button onClick={onClose} className="ml-auto p-1 text-stone-400 hover:text-stone-800"><X size={16} strokeWidth={1.5} /></button>
        </header>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[10px] label-eyebrow text-stone-500">For teammate</span>
            <select value={mt} onChange={(e) => setMt(e.target.value)} data-testid="grat-mt" className="mt-1 w-full px-3 py-2 subtle-border rounded-lg text-sm outline-none">
              {mts.map((m) => <option key={m.employee_id} value={m.employee_id}>{m.employee_name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] label-eyebrow text-stone-500">Amount ($)</span>
            <input type="number" step="1" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="grat-amount" className="mt-1 w-full px-3 py-2 subtle-border rounded-lg text-sm outline-none" />
          </label>
          <label className="block">
            <span className="text-[10px] label-eyebrow text-stone-500">Note (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="grat-note" placeholder="Guest name / room / etc." className="mt-1 w-full px-3 py-2 subtle-border rounded-lg text-sm outline-none" />
          </label>
        </div>
        <footer className="px-5 py-3 border-t border-stone-100 bg-stone-50 rounded-b-2xl">
          <button onClick={submit} disabled={busy} data-testid="grat-submit" className="w-full px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-40">
            {busy ? "Saving…" : "Log gratuity"}
          </button>
        </footer>
      </div>
    </div>
  );
}
